import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useGetInternalStockAvailabilityQuery } from "shared/redux/endpoints/procurementPurchases";
import { StatusBadge } from "../../ui";
import { getApiErrorMessage } from "../../orderSubmitFeedback";

// "Lấy từ kho nội bộ": both Merchandise warehouses' stock against what the
// source needs; picking one creates a 0đ proposal for that warehouse's supplier.
const InternalStockPicker = ({ open, params, busy, onClose, onPick }) => {
  const { data, isFetching, error } = useGetInternalStockAvailabilityQuery(
    params,
    { skip: !open || !params }
  );
  const warehouses = data || [];
  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Internal stock</DialogTitle>
      <DialogContent>
        <MDTypography variant="caption" color="text" display="block" mb={2}>
          0đ, no payment. Stock is deducted on issue.
        </MDTypography>
        {error ? (
          <MDTypography variant="button" color="error">
            {getApiErrorMessage(error, "Couldn't load warehouse stock.")}
          </MDTypography>
        ) : isFetching && !data ? (
          <MDTypography variant="button" color="text">
            Loading stock…
          </MDTypography>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={2}>
            {warehouses.map((w) => {
              const offCatalog = w.offCatalogLineIds?.length || 0;
              const noItems = !(w.items || []).length;
              const blocked = offCatalog > 0 || noItems;
              return (
                <MDBox
                  key={w.supplierId}
                  p={1.5}
                  borderRadius="lg"
                  sx={{ border: "1px solid", borderColor: "grey.300" }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    mb={1}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {w.warehouseName || `Kho #${w.warehouseCenterId}`}
                      </MDTypography>
                      {w.enough ? (
                        <StatusBadge status="approved" label="In stock" />
                      ) : (
                        <StatusBadge
                          status="rejected"
                          label="Short"
                          dot={false}
                        />
                      )}
                    </MDBox>
                    <MDButton
                      size="small"
                      variant="gradient"
                      color="info"
                      disabled={busy || blocked}
                      onClick={() => onPick(w)}
                    >
                      Use this warehouse
                    </MDButton>
                  </MDBox>
                  {offCatalog > 0 && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      display="block"
                      mb={1}
                    >
                      {offCatalog} off-catalog line(s) — move them to another PO.
                    </MDTypography>
                  )}
                  {noItems && !offCatalog && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      display="block"
                      mb={1}
                    >
                      No lines to transfer.
                    </MDTypography>
                  )}
                  {!blocked && !w.enough && (
                    <MDTypography
                      variant="caption"
                      color="warning"
                      display="block"
                      mb={1}
                    >
                      Not enough stock — Issue will be blocked.
                    </MDTypography>
                  )}
                  <table className="pc-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="pc-th-right">Needed</th>
                        <th className="pc-th-right">On hand</th>
                        <th className="pc-th-right">Short</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(w.items || []).map((it) => (
                        <tr key={it.itemId}>
                          <td>{it.itemName}</td>
                          <td className="pc-td-right">{it.neededQty}</td>
                          <td className="pc-td-right">{it.onHandQty}</td>
                          <td
                            className="pc-td-right"
                            style={
                              it.shortQty > 0
                                ? {
                                    color: "var(--pc-danger, #b42318)",
                                    fontWeight: 600,
                                  }
                                : undefined
                            }
                          >
                            {it.shortQty > 0 ? it.shortQty : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton
          variant="text"
          color="secondary"
          onClick={onClose}
          disabled={busy}
        >
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default InternalStockPicker;
